import { useEffect, useMemo, useState } from 'react'
import Toast from '../components/Toast.jsx'
import BackButton from '../components/BackButton.jsx'
import Modal from '../components/Modal.jsx'
import { listObras, createObra, updateObra, deleteObra, getStatusObraLabel, STATUS_OBRA } from '../data/obras.js'

export default function ObrasPage() {
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  
  // Estados de listagem
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('todos')
  
  // Estados de modal
  const [open, setOpen] = useState(false)
  const [editingObra, setEditingObra] = useState(null)
  const [busy, setBusy] = useState(false)
  const [deleteModal, setDeleteModal] = useState({ open: false, obra: null })
  
  // Form
  const [form, setForm] = useState({
    nome: '',
    codigo: '',
    cliente: '',
    endereco: '',
    dataInicio: '',
    dataPrevisaoFim: '',
    status: STATUS_OBRA.ativa
  })
  
  const filteredItems = useMemo(() => {
    const query = q.trim().toLowerCase()
    return items
      .filter(o => {
        if (statusFilter !== 'todos' && o.status !== statusFilter) return false
        if (!query) return true
        return (
          (o.nome ?? '').toLowerCase().includes(query) ||
          (o.codigo ?? '').toLowerCase().includes(query) ||
          (o.cliente ?? '').toLowerCase().includes(query)
        )
      })
      .sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? ''))
  }, [items, q, statusFilter])
  
  async function reload() {
    setLoading(true)
    try {
      const data = await listObras({ q, status: statusFilter })
      setItems(data)
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao carregar obras.')
    } finally {
      setLoading(false)
    }
  }
  
  useEffect(() => {
    reload()
  }, [q, statusFilter])
  
  function openNew() {
    setEditingObra(null)
    setForm({
      nome: '',
      codigo: '',
      cliente: '',
      endereco: '',
      dataInicio: '',
      dataPrevisaoFim: '',
      status: STATUS_OBRA.ativa
    })
    setOpen(true)
  }
  
  function openEdit(obra) {
    setEditingObra(obra)
    setForm({
      nome: obra.nome || '',
      codigo: obra.codigo || '',
      cliente: obra.cliente || '',
      endereco: obra.endereco || '',
      dataInicio: obra.dataInicio || '',
      dataPrevisaoFim: obra.dataPrevisaoFim || '',
      status: obra.status || STATUS_OBRA.ativa
    })
    setOpen(true)
  }
  
  async function onCreate(e) {
    e.preventDefault()
    if (!form.nome.trim() || !form.codigo.trim()) {
      setToastType('error')
      setToast('Nome e código são obrigatórios.')
      return
    }
    
    setBusy(true)
    try {
      await createObra(form)
      setToastType('success')
      setToast('Obra criada.')
      setOpen(false)
      setForm({
        nome: '',
        codigo: '',
        cliente: '',
        endereco: '',
        dataInicio: '',
        dataPrevisaoFim: '',
        status: STATUS_OBRA.ativa
      })
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao criar.')
    } finally {
      setBusy(false)
    }
  }
  
  async function onUpdate(e) {
    e.preventDefault()
    if (!form.nome.trim() || !form.codigo.trim()) {
      setToastType('error')
      setToast('Nome e código são obrigatórios.')
      return
    }
    
    setBusy(true)
    try {
      await updateObra(editingObra.id, form)
      setToastType('success')
      setToast('Obra atualizada.')
      setOpen(false)
      setEditingObra(null)
      setForm({
        nome: '',
        codigo: '',
        cliente: '',
        endereco: '',
        dataInicio: '',
        dataPrevisaoFim: '',
        status: STATUS_OBRA.ativa
      })
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao atualizar.')
    } finally {
      setBusy(false)
    }
  }
  
  async function onDelete() {
    if (!deleteModal.obra) return
    
    setBusy(true)
    try {
      await deleteObra(deleteModal.obra.id)
      setToastType('success')
      setToast('Obra finalizada.')
      setDeleteModal({ open: false, obra: null })
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao finalizar.')
    } finally {
      setBusy(false)
    }
  }
  
  const statusCounts = useMemo(() => {
    return {
      ativa: items.filter(o => o.status === STATUS_OBRA.ativa).length,
      finalizada: items.filter(o => o.status === STATUS_OBRA.finalizada).length,
      parada: items.filter(o => o.status === STATUS_OBRA.parada).length
    }
  }, [items])
  
  return (
    <div className="space-y-4">
      <BackButton />
      
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold">🏗️ Obras</h1>
          <p className="text-xs text-white/70">Centros de Custo</p>
        </div>
        <button
          onClick={openNew}
          className="rounded-xl bg-accent-600 px-3 py-2 text-xs font-semibold"
        >
          + Nova Obra
        </button>
      </div>
      
      {/* Cards de resumo */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-green-500/20 border border-green-500/30 p-3 text-center">
          <div className="text-lg font-bold text-green-400">{statusCounts.ativa}</div>
          <div className="text-[10px] text-green-300/80">Ativas</div>
        </div>
        <div className="rounded-xl bg-blue-500/20 border border-blue-500/30 p-3 text-center">
          <div className="text-lg font-bold text-blue-400">{statusCounts.finalizada}</div>
          <div className="text-[10px] text-blue-300/80">Finalizadas</div>
        </div>
        <div className="rounded-xl bg-yellow-500/20 border border-yellow-500/30 p-3 text-center">
          <div className="text-lg font-bold text-yellow-400">{statusCounts.parada}</div>
          <div className="text-[10px] text-yellow-300/80">Paradas</div>
        </div>
      </div>
      
      {/* Filtros */}
      <div className="space-y-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar obra..."
          className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none"
        />
        <div className="flex gap-2">
          {['todos', 'ativa', 'finalizada', 'parada'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex-1 rounded-lg px-2 py-1 text-[10px] font-medium ${
                statusFilter === s
                  ? 'bg-accent-600 text-white'
                  : 'bg-white/10 text-white/70'
              }`}
            >
              {s === 'todos' ? 'Todas' : getStatusObraLabel(s).split(' ')[1]}
            </button>
          ))}
        </div>
      </div>
      
      {/* Lista */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8 text-sm text-white/60">Carregando...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-sm text-white/60">
            {q ? 'Nenhuma obra encontrada.' : 'Nenhuma obra cadastrada.'}
          </div>
        ) : (
          filteredItems.map((obra) => (
            <div
              key={obra.id}
              className="rounded-2xl cm-card p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🏗️</span>
                    <div className="truncate text-sm font-semibold">{obra.nome}</div>
                  </div>
                  <div className="mt-1 text-xs text-white/70">
                    {obra.codigo}
                    {obra.cliente && ` • ${obra.cliente}`}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                      obra.status === STATUS_OBRA.ativa
                        ? 'bg-green-500/30 text-green-300'
                        : obra.status === STATUS_OBRA.finalizada
                        ? 'bg-blue-500/30 text-blue-300'
                        : 'bg-yellow-500/30 text-yellow-300'
                    }`}>
                      {getStatusObraLabel(obra.status)}
                    </span>
                  </div>
                  {obra.dataInicio && (
                    <div className="mt-1 text-[10px] text-white/50">
                      Início: {new Date(obra.dataInicio).toLocaleDateString('pt-BR')}
                      {obra.dataPrevisaoFim && ` • Previsão: ${new Date(obra.dataPrevisaoFim).toLocaleDateString('pt-BR')}`}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 flex-col gap-1">
                  <button
                    onClick={() => openEdit(obra)}
                    className="rounded-lg bg-white/10 px-2 py-1 text-[10px] text-white/80 hover:bg-white/20"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => setDeleteModal({ open: true, obra })}
                    className="rounded-lg bg-red-500/20 px-2 py-1 text-[10px] text-red-300 hover:bg-red-500/30"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Modal Cadastro */}
      <Modal
        open={open}
        title={editingObra ? 'Editar Obra' : 'Nova Obra'}
        onClose={() => {
          setOpen(false)
          setEditingObra(null)
          setForm({
            nome: '',
            codigo: '',
            cliente: '',
            endereco: '',
            dataInicio: '',
            dataPrevisaoFim: '',
            status: STATUS_OBRA.ativa
          })
        }}
      >
        <form className="space-y-3" onSubmit={editingObra ? onUpdate : onCreate}>
          <div>
            <div className="text-xs text-white/70">Nome da Obra *</div>
            <input
              value={form.nome}
              onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              placeholder="Ex: Condomínio Solar"
              required
            />
          </div>
          
          <div>
            <div className="text-xs text-white/70">Código *</div>
            <input
              value={form.codigo}
              onChange={(e) => setForm(f => ({ ...f, codigo: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              placeholder="Ex: OB-2026-001"
              required
            />
          </div>
          
          <div>
            <div className="text-xs text-white/70">Cliente</div>
            <input
              value={form.cliente}
              onChange={(e) => setForm(f => ({ ...f, cliente: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              placeholder="Ex: Construtora ABC"
            />
          </div>
          
          <div>
            <div className="text-xs text-white/70">Endereço</div>
            <input
              value={form.endereco}
              onChange={(e) => setForm(f => ({ ...f, endereco: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              placeholder="Ex: Rua ABC, 123 - Centro"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-white/70">Data Início</div>
              <input
                type="date"
                value={form.dataInicio}
                onChange={(e) => setForm(f => ({ ...f, dataInicio: e.target.value }))}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              />
            </div>
            <div>
              <div className="text-xs text-white/70">Previsão Término</div>
              <input
                type="date"
                value={form.dataPrevisaoFim}
                onChange={(e) => setForm(f => ({ ...f, dataPrevisaoFim: e.target.value }))}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              />
            </div>
          </div>
          
          {editingObra && (
            <div>
              <div className="text-xs text-white/70">Status</div>
              <select
                value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
              >
                <option value={STATUS_OBRA.ativa}>🟢 Ativa</option>
                <option value={STATUS_OBRA.finalizada}>🔵 Finalizada</option>
                <option value={STATUS_OBRA.parada}>🟡 Parada</option>
              </select>
            </div>
          )}
          
          <button
            disabled={busy}
            className="w-full rounded-xl bg-accent-600 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? 'Salvando…' : 'Salvar'}
          </button>
        </form>
      </Modal>
      
      {/* Modal Confirmação Exclusão */}
      {deleteModal.open && (
        <Modal
          open={deleteModal.open}
          title="Finalizar Obra?"
          onClose={() => setDeleteModal({ open: false, obra: null })}
        >
          <div className="space-y-4">
            <p className="text-sm text-white/80">
              Deseja finalizar a obra <strong>{deleteModal.obra?.nome}</strong>?
            </p>
            <p className="text-xs text-white/60">
              A obra será marcada como "Finalizada" e não aparecerá mais nas seleções de nova movimentação.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteModal({ open: false, obra: null })}
                className="flex-1 rounded-xl bg-white/10 px-3 py-2 text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={onDelete}
                disabled={busy}
                className="flex-1 rounded-xl bg-red-500 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {busy ? 'Finalizando…' : 'Finalizar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
